import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { StatusBadge, useToast } from "@cx/ui";
import type { GnreGenerateResult, GnreInvoicePlan } from "@cx/contracts";
import { formatBRL, formatDateBR } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { GNRE_STATUS_LABEL, GNRE_STATUS_TONE, guideTitle } from "./labels";

/** Bloqueios que só dizem "esta nota não tem guia": o painel some (a maioria das notas é dentro do estado). */
const QUIET = new Set(["not_outbound", "not_interstate", "nothing_due"]);

/**
 * Painel "Guias GNRE/DARE" da NF-e (T2.11): o que a nota deve à UF de destino (ICMS-ST, DIFAL, FCP), as guias já
 * geradas e o botão "Gerar guias" (uma por UF/receita; cria as contas a pagar). Uma Idempotency-Key por tela.
 */
export function InvoiceGnrePanel({
  fiscalDocumentId,
  invoiceStatus,
}: {
  fiscalDocumentId: string;
  invoiceStatus: string;
}): ReactElement | null {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const [genKey, setGenKey] = useState(() => crypto.randomUUID());
  const enabled =
    can(perms, "gnre", "read") && (invoiceStatus === "authorized" || invoiceStatus === "canceled");
  const key = ["gnre", companyId, "invoice", fiscalDocumentId, invoiceStatus];
  const plan = useQuery({
    queryKey: key,
    enabled,
    queryFn: () => api<GnreInvoicePlan>(`/gnre/invoice/${fiscalDocumentId}`),
  });
  const generate = useMutation({
    mutationFn: () =>
      api<GnreGenerateResult>("/gnre/generate", {
        method: "POST",
        headers: { "Idempotency-Key": genKey },
        body: JSON.stringify({ fiscalDocumentId }),
      }),
    onSuccess: (r) => {
      setGenKey(crypto.randomUUID());
      qc.setQueryData(key, r.plan);
      void qc.invalidateQueries({ queryKey: ["gnre", companyId, "list"] });
      void qc.invalidateQueries({ queryKey: ["titles", companyId] });
      toast(
        "success",
        r.created.length === 1
          ? "1 guia gerada com a conta a pagar"
          : `${r.created.length} guias geradas com as contas a pagar`,
      );
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const p = plan.data;
  if (!enabled || !p) return null;
  const missing = p.candidates.filter((c) => !c.guideId);
  const loud = p.blockers.filter((b) => !QUIET.has(b.code));
  if (p.candidates.length === 0 && p.guides.length === 0 && loud.length === 0) return null;
  const canGenerate = can(perms, "gnre", "create") && can(perms, "payables", "create");
  return (
    <div className="box card p-3 mb-3" data-testid="invoice-gnre">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <h2 className="h6 mb-0">Guias GNRE/DARE</h2>
        {canGenerate && invoiceStatus === "authorized" && missing.length > 0 && (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={generate.isPending}
            onClick={() => generate.mutate()}
          >
            Gerar guias ({formatBRL(missing.reduce((s, c) => s + c.amountCents, 0))})
          </button>
        )}
      </div>
      {loud.map((b) => (
        <p key={b.code} className="small text-muted mb-2">
          {b.message}
        </p>
      ))}
      {invoiceStatus === "canceled" && p.guides.some((g) => g.status !== "canceled") && (
        <div className="alert alert-warning py-2 small">
          A NF-e foi cancelada: cancele as guias não pagas; as pagas pedem restituição ao estado.
        </div>
      )}
      {(p.guides.length > 0 || missing.length > 0) && (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0" aria-label="Guias da nota">
            <thead>
              <tr>
                <th scope="col">Guia</th>
                <th scope="col">Receita</th>
                <th scope="col">Vencimento</th>
                <th scope="col" className="text-end">
                  Valor
                </th>
                <th scope="col">Situação</th>
              </tr>
            </thead>
            <tbody>
              {p.guides.map((g) => (
                <tr key={g.id}>
                  <td>
                    <Link to="/vendas/gnre/$id" params={{ id: g.id }}>
                      {guideTitle(g)}
                    </Link>
                  </td>
                  <td>{g.revenueCode}</td>
                  <td>{formatDateBR(g.dueDate)}</td>
                  <td className="text-end">{formatBRL(g.amountCents)}</td>
                  <td>
                    <StatusBadge tone={GNRE_STATUS_TONE[g.status]}>{GNRE_STATUS_LABEL[g.status]}</StatusBadge>
                  </td>
                </tr>
              ))}
              {invoiceStatus === "authorized" &&
                missing.map((c) => (
                  <tr key={`${c.uf}-${c.revenue}`} className="text-muted">
                    <td>{guideTitle(c)}</td>
                    <td>{c.revenueCode}</td>
                    <td>{formatDateBR(c.dueDate)}</td>
                    <td className="text-end">{formatBRL(c.amountCents)}</td>
                    <td>A gerar</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
